import { authService } from "@/services";
import { userService } from "@/services/user.service";
import { loginResponse } from "@/services/api-schema";
import router from "@/router";
import { PASSWORD_CHANGE_RESULT_CODES } from "@/helpers/constants/authConstants";

export default {
  namespaced: true,
  state: {
    user: {},
    status: {
      loggedIn: false,
      loggingIn: false,
      error: false,
    },
    userSchemas: localStorage.getItem("user_schemas")
      ? JSON.parse(localStorage.getItem("user_schemas"))
      : [],
    merchant: "",
    subNickname: "",
    // Password change enforcement state
    passwordChangeRequired: false,
    passwordChangeContext: null,
    temporaryToken: null,
    // Additional state for enhanced password change enforcement
    passwordChangeInProgress: false,
    passwordChangeError: null,
  },
  getters: {
    getStatus(state) {
      return state.status;
    },
    getRASubRA(state) {
      let res = null;
      if (state.subNickname) {
        const sn = state.subNickname;
        res = sn.substr(0, sn.lastIndexOf(":"));
      }
      return res;
    },
    getSubRA(state) {
      let res = null;
      if (state.subNickname) {
        const sn = state.subNickname;
        res = sn.substring(sn.indexOf(":") + 1, sn.lastIndexOf(":"));
      }
      return res;
    },
    isPasswordChangeRequired(state) {
      return state.passwordChangeRequired;
    },
    getPasswordChangeContext(state) {
      return state.passwordChangeContext;
    },
    getTemporaryToken(state) {
      return state.temporaryToken;
    },
    isPasswordChangeInProgress(state) {
      return state.passwordChangeInProgress;
    },
    getPasswordChangeError(state) {
      return state.passwordChangeError;
    },
    // Enhanced getter to check if user should be blocked from navigation
    shouldBlockNavigation(state) {
      return state.passwordChangeRequired && !state.passwordChangeInProgress;
    },
  },
  mutations: {
    loginRequest(state, user) {
      state.status.loggingIn = true;
      state.status.error = false;
      state.user = user;
    },
    loginSuccess(state, { user, schemas, merchant, subNickname }) {
      state.status.loggedIn = true;
      state.status.loggingIn = false;
      state.status.error = false;
      state.user = user;
      state.userSchemas = schemas;
      state.merchant = merchant;
      state.subNickname = subNickname;
      // Clear password change state on successful login
      state.passwordChangeRequired = false;
      state.passwordChangeContext = null;
      state.temporaryToken = null;
      state.passwordChangeInProgress = false;
      state.passwordChangeError = null;
    },
    loginFailure(state, error) {
      state.status.loggedIn = false;
      state.status.loggingIn = false;
      state.status.error = error || true;
      state.user = null;
      // Clear password change state on login failure
      state.passwordChangeRequired = false;
      state.passwordChangeContext = null;
      state.temporaryToken = null;
      state.passwordChangeInProgress = false;
      state.passwordChangeError = null;
    },
    logout(state) {
      state.status.loggedIn = false;
      state.status.loggingIn = false;
      state.user = null;
      state.userSchemas = null;
      // Clear password change state on logout
      state.passwordChangeRequired = false;
      state.passwordChangeContext = null;
      state.temporaryToken = null;
      state.passwordChangeInProgress = false;
      state.passwordChangeError = null;
    },
    setPasswordChangeRequired(
      state,
      {
        resultCode,
        message,
        temporaryToken,
        passwordPolicy,
        originalRedirection,
      }
    ) {
      state.passwordChangeRequired = true;
      state.passwordChangeContext = {
        resultCode,
        message,
        passwordPolicy,
        originalRedirection,
      };
      state.temporaryToken = temporaryToken;
      // Clear error status so error notification doesn't show
      // state.status.loggingIn = false;
      // state.status.error = false;
    },
    clearPasswordChangeRequired(state) {
      state.passwordChangeRequired = false;
      state.passwordChangeContext = null;
      state.temporaryToken = null;
      state.passwordChangeInProgress = false;
      state.passwordChangeError = null;
    },
    setPasswordChangeInProgress(state, inProgress) {
      state.passwordChangeInProgress = inProgress;
      if (inProgress) {
        state.passwordChangeError = null;
      }
    },
    setPasswordChangeError(state, error) {
      state.passwordChangeError = error;
      state.passwordChangeInProgress = false;
    },
  },
  actions: {
    login({ commit }, { username, password, redirection }) {
      commit("loginRequest", { username });

      authService.login(username, password).then(
        (user) => {
          console.log("Login success - user data:", user);
          console.log(
            "Login success - requiresPasswordChange:",
            user.requiresPasswordChange
          );

          // Check if password change is required
          // authService catches password change errors and returns them as success with requiresPasswordChange flag
          if (user.requiresPasswordChange) {
            console.log(
              "Password change required - setting state and navigating"
            );

            // Commit password change state (this also clears error status)
            commit("setPasswordChangeRequired", {
              resultCode: user.passwordChangeResultCode,
              message: user.passwordChangeMessage,
              temporaryToken: user.temporaryToken,
              passwordPolicy: user.passwordPolicy,
              originalRedirection: redirection || "/dashboard",
            });
            // Set user in localStorage so navigation guard allows access
            localStorage.setItem("user", username);
            console.log("Navigating to /dashboard/configuration");
            // Navigate to password change page directly without going through the guard's redirect logic
            router.replace("/dashboard/configuration").catch((err) => {
              // Ignore navigation errors
              if (err.name !== "NavigationDuplicated") {
                console.error("Navigation error:", err);
              }
            });
          } else {
            console.log("Normal login - navigating to dashboard");

            // Normal login success
            commit("loginSuccess", {
              user: username,
              schemas: user[loginResponse.userSchemas],
              merchant: user[loginResponse.merchant],
              subNickname: user[loginResponse.subNickname],
            });
            // Navigate to intended destination or default dashboard
            router.push(redirection || "/dashboard");
          }
        },
        (error) => {
          commit("loginFailure", error);
          // dispatch("alert/error", error, { root: true });
        }
      );
    },

    async changePasswordDuringLogin(
      { commit, state, dispatch },
      { username, oldPassword, newPassword }
    ) {
      try {
        // Set password change in progress
        commit("setPasswordChangeInProgress", true);

        const result = await userService.changePasswordDuringLogin(
          oldPassword,
          newPassword,
          state.temporaryToken
        );

        // Password change successful, complete the login
        commit("loginSuccess", {
          user: username,
          schemas: result[loginResponse.userSchemas],
          merchant: result[loginResponse.merchant],
          subNickname: result[loginResponse.subNickname],
        });

        // Fetch menu now that we have a valid permanent token
        dispatch("menu/fetchMenu", null, { root: true });

        // Redirect to original destination
        const originalRedirection =
          state.passwordChangeContext?.originalRedirection || "/dashboard";
        router.push(originalRedirection);

        return result;
      } catch (error) {
        // Handle password change errors
        commit(
          "setPasswordChangeError",
          error.message || "Password change failed"
        );
        commit("setPasswordChangeInProgress", false);
        throw error;
      }
    },

    // Action to retry password change after error
    retryPasswordChange({ commit }) {
      commit("setPasswordChangeError", null);
    },

    // Action to check if specific result codes require password change
    isPasswordChangeResultCode(context, resultCode) {
      return PASSWORD_CHANGE_RESULT_CODES.includes(resultCode);
    },

    logout({ commit }) {
      authService.logout();
      commit("logout");
    },
  },
};
